import { useEffect, useRef, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { Button } from "react-bootstrap";
import * as yup from "yup";

const ALLOWED_TYPES = [
    "text/csv",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
];

const ALLOWED_EXTENSIONS = [".csv", ".xlsx"];

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

/**
 * Yup schema for file validation
 * @param {number} maxSize - Max file size in bytes
 */
const createFileSchema = (maxSize = MAX_FILE_SIZE) =>
    yup.object().shape({
        file: yup
            .mixed()
            .required("Please select a file to import")
            .test("fileType", "Only CSV and XLSX files are allowed", (value) => {
                if (!value) return false;
                const ext = `.${value.name.split(".").pop().toLowerCase()}`;
                return ALLOWED_EXTENSIONS.includes(ext) || ALLOWED_TYPES.includes(value.type);
            })
            .test("fileSize", "File size must be less than 5MB", (value) => {
                if (!value) return false;
                return value.size <= maxSize;
            }),
    });

/**
 * @typedef {Object} ImportModalProps
 * @property {boolean} show - Controls modal visibility
 * @property {() => void} handleClose - Function to close the modal
 * @property {string} [title] - Modal title (e.g. "Import Students")
 * @property {string} [description] - Helper text shown below title
 * @property {(file: File) => Promise<any>} onImport - Import function that receives the file
 * @property {boolean} loading - Loading state from the hook
 * @property {string} error - Error message from the hook
 * @property {string} [accept] - Accepted file types (default: .csv,.xlsx,.xls)
 * @property {number} [maxFileSize] - Max file size in bytes (default: 5MB)
 */

/**
 * A reusable import modal component styled consistently with SuccessModal.
 * Handles file selection UI with Yup validation and delegates the actual
 * import to the parent via the onImport callback.
 *
 * @param {ImportModalProps} props
 */
const ImportModal = ({
    show,
    handleClose,
    title = "Import File",
    description = "Upload a CSV or Excel file to import data.",
    onImport,
    loading = false,
    error = "",
    accept = ".csv,.xlsx",
    maxFileSize = MAX_FILE_SIZE,
}) => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [dragActive, setDragActive] = useState(false);
    const [validationError, setValidationError] = useState("");
    const fileInputRef = useRef(null);
    const fileSchema = createFileSchema(maxFileSize);

    // Reset state when modal opens
    useEffect(() => {
        if (show) {
            setSelectedFile(null);
            setValidationError("");
            setDragActive(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
        }
    }, [show]);

    const validateFile = async (file) => {
        try {
            await fileSchema.validate({ file });
            setValidationError("");
            return true;
        } catch (err) {
            setValidationError(err.message);
            return false;
        }
    };

    const handleFileChange = async (e) => {
        const file = e.target.files?.[0];
        if (file) {
            const isValid = await validateFile(file);
            if (isValid) {
                setSelectedFile(file);
            } else {
                setSelectedFile(null);
            }
        }
    };

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        const file = e.dataTransfer.files?.[0];
        if (file) {
            const isValid = await validateFile(file);
            if (isValid) {
                setSelectedFile(file);
            } else {
                setSelectedFile(null);
            }
        }
    };

    const handleRemoveFile = () => {
        setSelectedFile(null);
        setValidationError("");
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const handleImport = async () => {
        if (!selectedFile || !onImport) return;
        await onImport(selectedFile);
    };

    const handleModalClose = () => {
        if (loading) return;
        setSelectedFile(null);
        setValidationError("");
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
        handleClose();
    };

    const formatFileSize = (bytes) => {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    const displayError = validationError || error;

    return (
        <Modal
            show={show}
            onHide={handleModalClose}
            centered
            className="custom-modal"
            backdrop="static"
            keyboard={false}
        >
            <Modal.Body className="p-2 m-4">
                {/* Title */}
                <div className="text-center mb-2">
                    <h5 className="text-success fw-bold fs-3">{title}</h5>
                </div>

                {/* Description */}
                <p className="text-center mb-4 mt-2 pt-2 custom-modal-description">
                    {description}
                </p>

                {/* File Upload Area */}
                <div
                    className={`import-modal-dropzone ${dragActive ? "import-modal-dropzone-active" : ""}`}
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    style={{
                        border: `2px dashed ${dragActive ? "#008ef4" : validationError ? "#dc3545" : "#d9d9d9"}`,
                        borderRadius: "12px",
                        padding: "30px 20px",
                        textAlign: "center",
                        cursor: "pointer",
                        backgroundColor: dragActive ? "#f0f9ff" : "#fafafa",
                        transition: "all 0.2s ease",
                    }}
                >
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept={accept}
                        onChange={handleFileChange}
                        style={{ display: "none" }}
                    />

                    {!selectedFile ? (
                        <div>
                            <div style={{ fontSize: "36px", marginBottom: "8px" }}>📁</div>
                            <p
                                style={{
                                    margin: 0,
                                    color: "#667085",
                                    fontSize: "14px",
                                }}
                            >
                                Drag & drop your file here, or{" "}
                                <span
                                    style={{
                                        color: "#008ef4",
                                        fontWeight: 600,
                                        textDecoration: "underline",
                                    }}
                                >
                                    browse
                                </span>
                            </p>
                            <p
                                style={{
                                    margin: "4px 0 0",
                                    color: "#98a2b3",
                                    fontSize: "12px",
                                }}
                            >
                                Supported: CSV, XLSX (Max{" "}
                                {formatFileSize(maxFileSize)})
                            </p>
                        </div>
                    ) : (
                        <div
                            onClick={(e) => e.stopPropagation()}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                backgroundColor: "#fff",
                                border: "1px solid #e4e7ec",
                                borderRadius: "8px",
                                padding: "10px 14px",
                            }}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "10px",
                                    overflow: "hidden",
                                }}
                            >
                                <span style={{ fontSize: "20px" }}>📄</span>
                                <div style={{ overflow: "hidden" }}>
                                    <p
                                        style={{
                                            margin: 0,
                                            fontWeight: 500,
                                            fontSize: "14px",
                                            color: "#344054",
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                        }}
                                    >
                                        {selectedFile.name}
                                    </p>
                                    <p
                                        style={{
                                            margin: 0,
                                            fontSize: "12px",
                                            color: "#98a2b3",
                                        }}
                                    >
                                        {formatFileSize(selectedFile.size)}
                                    </p>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={handleRemoveFile}
                                disabled={loading}
                                style={{
                                    background: "none",
                                    border: "none",
                                    color: "#dc3545",
                                    cursor: "pointer",
                                    fontSize: "18px",
                                    padding: "4px",
                                    flexShrink: 0,
                                }}
                                title="Remove file"
                            >
                                ✕
                            </button>
                        </div>
                    )}
                </div>

                {/* Error message */}
                {displayError && (
                    <div className="custom-error mt-3 text-center">{displayError}</div>
                )}

                {/* Action Buttons */}
                <div className="d-flex flex-column flex-md-row gap-3 justify-content-center mt-4">
                    <button
                        className="custom-cancel-button"
                        onClick={handleModalClose}
                        disabled={loading}
                    >
                        Cancel
                    </button>
                    <Button
                        className="custom-primary-button"
                        onClick={handleImport}
                        disabled={!selectedFile || loading || !!validationError}
                    >
                        {loading ? "Importing..." : "Import"}
                    </Button>
                </div>
            </Modal.Body>
        </Modal>
    );
};

export default ImportModal;
